import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'
import {useIntl} from 'react-intl'
import Logo from 'src/_metronic/assets/everyvisit-logo.svg'
import BG_CONTAINER from '../asset/circle-container.jpg'
import MAIN from '../asset/main.jpg'
import MAIN_2 from '../asset/main-2.jpeg'
import BG from '../asset/bg-login.png'
import {LanguageList} from '../helper/languageList'
import {GoogleReCaptchaCheckbox} from 'react-google-recaptcha-ultimate'
import {useLogin} from '../context/useMutation'
import {useState} from 'react'
import LogoMc from '../../../_metronic/assets/mc-logo.svg'

const developmentMode = process.env.NODE_ENV === 'development'

function SignIn() {
  const intl = useIntl()
  const {mutate, isLoading} = useLogin()
  const [oldgoogleRecaptcha, setOldGoogleRecaptcha] = useState<string>('')
  const [passwordShown, setPasswordShown] = useState(false)

  // Floating animation keyframes
  const floatingStyle = `
    @keyframes float {
      0% { transform: translateY(0px) translateX(0px); }
      50% { transform: translateY(-20px) translateX(10px); }
      100% { transform: translateY(0px) translateX(0px); }
    }
    @keyframes float-reverse {
      0% { transform: translateY(0px) translateX(0px); }
      50% { transform: translateY(15px) translateX(-15px); }
      100% { transform: translateY(0px) translateX(0px); }
    }
  `

  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required')
      .max(100, intl.formatMessage({id: 'GLOBAL.VALIDATION.MAX_LENGTH_FIELD'}, {length: 100})),
    password: Yup.string()
      .required('Password is required')
      .max(50, 'Password must be less than 50 characters'),
    // recaptcha: Yup.string().required('Captcha is required'),
    // recaptcha require but email mehmet@gmail.com and password 1234567890 not require
    recaptcha: Yup.string().when(['email'], ([email], schema) => {
      return email?.includes('mehmet.baran')
        ? schema.notRequired()
        : schema.required('Captcha is required')
    }),
  })

  const onSubmit = async (values) => {
    const reCaptchaValue = values.email.includes('mehmet.baran')
      ? '20b68015c5921MEMOLI3ffd35c4b6b644a4d1b'
      : values.recaptcha

    setOldGoogleRecaptcha(values.recaptcha)
    mutate({
      values: {
        ...values,
        recaptcha: reCaptchaValue,
      },
      recaptcha: oldgoogleRecaptcha,
    })
  }

  const handleExpired = (setFieldValue) => {
    setOldGoogleRecaptcha('')
    setFieldValue('recaptcha', '')
    if (typeof document !== 'undefined' && (window as any).grecaptcha) {
      ;(window as any).grecaptcha.reset()
    }
  }

  return (
    <>
      <style>{floatingStyle}</style>
      <div className='d-flex flex-column flex-root' id='kt_app_root'>
        <div className='d-flex flex-column flex-lg-row flex-column-fluid'>
          <a href='sign-in' className='d-block d-lg-none mx-auto pt-10'>
            <div className='d-flex  gap-3 align-items-center justify-content-center'>
              <img alt='Logo' src={LogoMc} className='h-20px' />
              <img alt='Logo' src={Logo} className='h-25px' />
            </div>
          </a>

          <div className='d-flex flex-column flex-column-fluid flex-center w-lg-50 p-10'>
            <div className='d-flex justify-content-center flex-column-fluid flex-column w-100 mw-550px gap-20'>
              <div className='d-flex flex-stack py-2'>
                <div className='m-0'>
                  <div className='d-none d-md-block'>
                    <div className='d-flex  gap-3 align-items-center justify-content-center'>
                      <img alt='Logo' src={LogoMc} className='h-25px' />
                      <img alt='Logo' src={Logo} className='h-35px' />
                    </div>
                  </div>
                </div>
              </div>

              <div className=''>
                <Formik
                  initialValues={{
                    email: '',
                    password: '',
                    recaptcha: '',
                  }}
                  validationSchema={validationSchema}
                  onSubmit={onSubmit}
                >
                  {({errors, touched, setFieldValue}) => (
                    <Form className='form w-100'>
                      <div className='card-body'>
                        <div className='text-start mb-10'>
                          <h1 className='text-primary mb-3 fs-3x' data-kt-translate='sign-in-title'>
                            Good to see you.
                          </h1>

                          <div
                            className='text-gray-400 fw-semibold fs-6'
                            data-kt-translate='general-desc'
                          >
                            Sign in to build loyalty, drive repeat visits, and grow revenue.
                          </div>
                        </div>

                        <div className='fv-row mb-8 form-floating'>
                          <Field
                            name='email'
                            className='form-control'
                            type='text'
                            id='floatingInput'
                            placeholder='name@example.com'
                            maxLength={101}
                            onKeyUp={(e) => {
                              const value = e.target.value.toLowerCase()
                              const noTurkishChars = value.replaceAll(/[ğĞüÜşŞıİöÖçÇ]/g, '')
                              setFieldValue('email', noTurkishChars)
                            }}
                          />
                          <label>{intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}</label>
                          {errors.email && touched.email ? (
                            <div className='fv-plugins-message-container'>
                              <div className='fv-help-block'>{errors.email}</div>
                            </div>
                          ) : null}
                        </div>

                        <div className='fv-row mb-7 form-floating'>
                          <div
                            className='position-absolute top-50 end-0 translate-middle-y fs-10 pe-6'
                            style={{cursor: 'pointer'}}
                            onClick={() => setPasswordShown(!passwordShown)}
                          >
                            <i
                              className={`bi ${passwordShown ? 'bi-eye-slash' : 'bi-eye'}`}
                              style={{fontSize: '1.4rem'}}
                            ></i>
                          </div>
                          <Field
                            id='password'
                            name='password'
                            type={passwordShown ? 'text' : 'password'}
                            className='form-control'
                            placeholder=''
                          />
                          <label>{intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'})}</label>
                          {errors.password && touched.password ? (
                            <div className='fv-plugins-message-container'>
                              <div className='fv-help-block'>{errors.password}</div>
                            </div>
                          ) : null}
                        </div>

                        <div className='mb-7'>
                          <GoogleReCaptchaCheckbox
                            action='LOGIN'
                            callback={(token) => {
                              setFieldValue('recaptcha', token)

                              setTimeout(() => {
                                handleExpired(setFieldValue)
                              }, 60000)
                            }}
                          />
                          {errors.recaptcha && touched.recaptcha ? (
                            <div className='fv-plugins-message-container'>
                              <div className='fv-help-block'>{errors.recaptcha}</div>
                            </div>
                          ) : null}
                        </div>

                        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-10'>
                          <div>
                            <div className='d-flex flex-stack'>
                              <button
                                disabled={isLoading}
                                className='btn btn-primary me-2 flex-shrink-0'
                              >
                                <i className='bi bi-arrow-right me-3 fs-3'></i>
                                <span
                                  className='indicator-label'
                                  data-kt-translate='sign-in-submit'
                                >
                                  {isLoading ? (
                                    <span data-kt-translate='general-progress'>
                                      {intl.formatMessage({id: 'LOADING'})}
                                    </span>
                                  ) : (
                                    intl.formatMessage({id: 'AUTH.LOGIN.TITLE'})
                                  )}
                                </span>

                                <span className='indicator-progress'>
                                  <span data-kt-translate='general-progress'>
                                    {intl.formatMessage({id: 'LOADING'})}
                                  </span>
                                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                </span>
                              </button>
                            </div>
                          </div>

                          <a
                            className='border-bottom border-2 border-secondary'
                            href='#'
                            data-kt-translate='sign-in-forgot-password'
                          >
                            {intl.formatMessage({id: 'AUTH.GENERAL.FORGOT_BUTTON'})}
                          </a>
                        </div>
                      </div>
                      <div className='d-flex flex-stack'>
                        <a
                          className='p-6 rounded w-100 text-white d-flex flex-column flex-start bg-secondary text-hover-white'
                          href='#'
                        >
                          <div className='d-flex flex-row align-items-center gap-2'>
                            <div className='d-flex align-items-center'>
                              <i className='bi bi-arrow-right-circle me-3 fs-1 text-white'></i>
                            </div>
                            <div className='d-flex flex-column'>
                              <span className='fs-2 fw-bold'>
                                {intl.formatMessage({id: 'AUTH.LOGIN.BECOME_MERCHANT'})}
                              </span>
                              <span className='fs-6'>Set up your loyalty program in minutes.</span>
                            </div>
                          </div>
                        </a>
                      </div>
                      <div className='mt-10'>
                        <LanguageList />
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>

          <div
            className='d-none d-lg-flex flex-lg-row-fluid w-50 bgi-size-cover bgi-position-center bgi-no-repeat'
            style={{
              backgroundImage: `url('${BG_CONTAINER}')`,
            }}
          >
            <div className='d-flex flex-column flex-center p-15 w-100 position-relative'>
              <div
                className='rounded-circle shadow-lg d-flex flex-center position-absolute'
                style={{
                  width: '380px',
                  height: '380px',
                  top: '12%',
                  left: '12%',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(15px)',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                  zIndex: 2,
                }}
              >
                <img
                  src={MAIN}
                  alt='Mastercard Solutions'
                  className='w-100 h-100'
                  style={{objectFit: 'cover'}}
                />
              </div>

              <div
                className='rounded-circle shadow-lg d-flex flex-center position-absolute'
                style={{
                  width: '300px',
                  height: '300px',
                  bottom: '12%',
                  right: '12%',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(15px)',
                  overflow: 'hidden',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                  zIndex: 1,
                }}
              >
                <img
                  src={MAIN_2}
                  alt='Mastercard Solutions 2'
                  className='w-100 h-100'
                  style={{objectFit: 'cover'}}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SignIn
